import { useEffect, useState } from "react";
import { TrendingDown, TrendingUp, Check, Loader2, Sparkles, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { aiPriceCoach } from "@/lib/api";

/**
 * Price coach badge — appears below the price field in DeposerPage.
 * Pro users get live AI analysis vs market; non-Pro see a teaser CTA.
 */
export default function PriceCoach({ data, isPro }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showUpsell, setShowUpsell] = useState(false);

  // Auto-analyse 1.5s after last change of (price, category, city) — Pro only.
  useEffect(() => {
    if (!isPro) { setResult(null); return; }
    const price = parseInt(data.price || "0", 10);
    if (!price || !data.category || !data.city) { setResult(null); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await aiPriceCoach({
          title: data.title || "",
          category: data.category,
          city: data.city,
          current_price: price,
        });
        setResult(r);
      } catch { setResult(null); }
      finally { setLoading(false); }
    }, 1500);
    return () => clearTimeout(t);
  }, [isPro, data.price, data.category, data.city, data.title]);

  if (!data.price || parseInt(data.price, 10) <= 0) return null;

  if (!isPro) {
    return (
      <div className="mt-2" data-testid="price-coach-locked">
        <button
          type="button"
          onClick={() => setShowUpsell(true)}
          className="w-full inline-flex items-center gap-2 p-3 rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50/40 text-left text-xs text-amber-800 hover:bg-amber-50"
        >
          <Sparkles size={14} className="text-amber-600 shrink-0" />
          <span>Est-ce le bon prix ? L'IA vous donne une fourchette marché basée sur des annonces similaires.</span>
          <Crown size={12} className="text-amber-600 ml-auto shrink-0" />
        </button>
        {showUpsell && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowUpsell(false)}>
            <div className="bg-white rounded-3xl p-6 max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <Crown className="mx-auto text-amber-500 mb-3" size={40} />
              <h3 className="text-xl font-bold text-center mb-2">Price Coach Pro</h3>
              <p className="text-sm text-[var(--ink-soft)] text-center mb-5">
                Réservé aux abonnés Fodindin Pro. L'IA compare votre prix au marché UEMOA en temps réel.
              </p>
              <Link to="/abonnements/pro" className="inline-flex items-center justify-center gap-2 w-full py-3 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold">
                <Crown size={14} /> Devenir Pro
              </Link>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 text-xs text-amber-700" data-testid="price-coach-loading">
        <Loader2 size={12} className="animate-spin" /> L'IA analyse le marché…
      </div>
    );
  }

  if (!result) return null;

  const palette = {
    underpriced: { bg: "bg-blue-50",   ring: "border-blue-300",    text: "text-blue-700",    Icon: TrendingDown, label: "Sous-évalué" },
    fair:        { bg: "bg-emerald-50", ring: "border-emerald-300", text: "text-emerald-700", Icon: Check,        label: "Bon prix"   },
    overpriced:  { bg: "bg-red-50",     ring: "border-red-300",     text: "text-red-700",     Icon: TrendingUp,   label: "Trop cher"  },
  };
  const p = palette[result.assessment] || palette.fair;

  return (
    <div className={`mt-2 rounded-2xl p-3 border-2 ${p.ring} ${p.bg}`} data-testid={`price-coach-${result.assessment}`}>
      <div className={`inline-flex items-center gap-1.5 text-xs font-bold ${p.text}`}>
        <p.Icon size={14} /> {p.label}
      </div>
      <p className={`text-xs mt-1 ${p.text}`}>{result.reasoning}</p>
      {result.suggested_min && result.suggested_max && (
        <p className="text-[11px] text-[var(--ink-soft)] mt-1">
          Fourchette suggérée : <b>{result.suggested_min.toLocaleString("fr-FR")} – {result.suggested_max.toLocaleString("fr-FR")} FCFA</b>
        </p>
      )}
    </div>
  );
}
